import React, { useEffect, useMemo, useState } from 'react'
import { RouteComponentProps } from 'react-router'
import {
  useColumnOrder,
  useFilters,
  useGlobalFilter,
  usePagination,
  useRowSelect,
  useSortBy,
  useTable,
} from 'react-table'
import moment from 'moment-timezone'
import map from 'lodash/map'
import filter from 'lodash/filter'
import { Grid } from '@material-ui/core'

import Loading from 'components/Loading'

import { NumberCell } from 'core/table/cells'
import DEFAULT_FILTERS from 'core/table/filters'
import { ExtendedPagedTable, useFiltersFromQuery } from 'core/table/components'
import {
  useConditionalSelectColumn,
  useGetColumnState,
  useTableColumnStyles as useStyles,
} from 'core/table/table-hooks'
import { NUMBER_OPERATOR_OPTIONS, operatorTypes, STRING_OPERATOR_OPTIONS } from 'core/table/interfaces/Filters'

import {
  useFetchMeetingsGrid,
  useMentorshipMeetingsColumns as useColumns,
  useMentorshipMeetingsSummary,
} from '../hooks'
import { ExportMentorshipMeetingsButton } from '../components/ExportMentorshipMeetingsButton'
import { useWithinRange } from 'utils/useWithinRange'

import PageHeader from 'App/components/PageHeader'
import MENTORSHIP_FILTERS from 'new-mentorship/helpers/filters'
import { useDateRangeFilter } from 'filtering/hooks'
import { ProgramMeetingForGrid } from 'new-mentorship/types'

const getOperatorOptions = (column: string) => {
  switch (column) {
    case 'duration':
      return NUMBER_OPERATOR_OPTIONS
    case 'mentor':
    case 'mentee':
      return STRING_OPERATOR_OPTIONS
    case 'program':
    case 'rating':
      return [
        { value: operatorTypes.ANY_OF, displayValue: 'Includes' },
        { value: operatorTypes.NOT_ANY_OF, displayValue: 'Does not include' },
      ]
    case 'type':
      return [{ value: operatorTypes.IS, displayValue: 'Is' }]

    default:
      return []
  }
}

const cards = [
  {
    header: 'Matches',
    accessor: 'matchesCount',
    Cell: NumberCell,
  },
  {
    header: 'Meetings',
    accessor: 'meetingCount',
    Cell: NumberCell,
  },
  {
    header: 'Avg. Meeting Duration',
    accessor: 'averageMeetingTime',
    Cell: NumberCell,
  },
  {
    header: 'Positive Experiences',
    accessor: 'positiveFeedbackPercent',
  },
]

const tableName = 'mentorshipMeetings'

const NewMentorshipMeetingsComponent = ({ location, history }: RouteComponentProps) => {
  const { filters } = useFiltersFromQuery(location.search, getOperatorOptions)
  const [fetchColumnState, { loading: columnStateLoading, result: columnState }] = useGetColumnState()
  const columnStyles = useStyles()
  const columns: any = useColumns()
  const [dateRange] = useDateRangeFilter()
  const [filteredData, setFilteredData] = useState<ProgramMeetingForGrid[]>([])
  const [withinRange] = useWithinRange(dateRange)

  const [
    fetchMeetings,
    {
      loading,
      result: { meetings: meetingsUnmapped, questionColumns },
    },
  ] = useFetchMeetingsGrid()

  useEffect(() => {
    fetchMeetings()
  }, [fetchMeetings])

  useEffect(() => {
    fetchColumnState(tableName)
  }, [fetchColumnState])

  useEffect(() => {
    setFilteredData(filter(meetingsUnmapped, ({ startsAt, endsAt }) => withinRange(startsAt, endsAt)))
  }, [dateRange, meetingsUnmapped, withinRange])

  const meetings = useMemo(
    () =>
      map(
        filteredData,
        ({
          id,
          startsAt,
          endsAt,
          type,
          mentorName,
          menteeName,
          programName,
          mentorRating,
          menteeRating,
          answers,
          matchId,
        }) => ({
          id,
          startsAt,
          mentor: mentorName,
          mentee: menteeName,
          program: programName,
          type,
          duration: Math.round(moment.duration(moment(endsAt).diff(moment(startsAt))).as('minutes')),
          rating: { mentor: mentorRating, mentee: menteeRating },
          matchId,
          answers,
        }),
      ),
    [filteredData],
  )

  const table = useTable(
    {
      data: meetings,
      columns,
      // @ts-ignore
      filterTypes: { ...DEFAULT_FILTERS, ...MENTORSHIP_FILTERS },
      initialState: {
        // @ts-ignore
        filters,
        // @ts-ignore
        globalFilter: '',
      },
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination,
    useRowSelect,
    useColumnOrder,
    useConditionalSelectColumn(() => true, columnStyles.selectColumn),
  )

  const summary = useMentorshipMeetingsSummary(table.rows)

  if (loading || columnStateLoading) {
    return <Loading />
  }

  return (
    <Grid container>
      <ExtendedPagedTable
        summary={summary}
        cards={cards}
        history={history}
        location={location}
        table={table}
        columns={columns}
        getOperatorOptions={getOperatorOptions}
        columnState={columnState}
        tableName={tableName}
        searchPlaceholder="Search Meetings"
        filterSubHeader="Select meeting attributes to filter by:"
        buttons={
          <>
            <ExportMentorshipMeetingsButton data={table.selectedFlatRows} questionColumns={questionColumns} />
          </>
        }
        useGlobalSearch
        useFilters
        useDateRange
      />
    </Grid>
  )
}

export const NewMentorshipMeetings = (props: RouteComponentProps) => {
  return (
    <PageHeader title="Mentor Meetings" subTitle="View and export meeting information between mentors and mentees">
      <NewMentorshipMeetingsComponent {...props} />
    </PageHeader>
  )
}

export default NewMentorshipMeetings
